import moment from 'moment';
import { Select } from 'react-daisyui';
import { QBOCategory, QBODetailedTransaction } from '@shared/types/qbo_data';
import { TransactionAutoComplete } from '@/pages/Transactions/TransactionAutoComplete';

export const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const getAmount = (t: QBODetailedTransaction) =>
  (t.spent ? t.spent : 0) - (t.received ? t.received : 0);

export const formatDate = (date?: string) =>
  date ? moment(new Date(date.toString())).format('MM/DD/YY') : 'N/A';

/** Text that never exceeds a fixed width; full text shows in the native
 *  tooltip (title) on hover — never clipped, unlike a positioned tooltip. */
export function ClipText({
  value,
  width = 120,
}: {
  value?: string;
  width?: number;
}) {
  if (!value) {
    return <span className='text-base-content/40'>—</span>;
  }
  return (
    <div
      className='cursor-default overflow-hidden text-ellipsis whitespace-nowrap'
      style={{ width }}
      title={value}
    >
      {value}
    </div>
  );
}

/** One-line value with ellipsis that fills its (flexible) cell; the full text
 *  shows in a daisyUI tooltip on hover. The overflow (clipping) is on the inner
 *  span so the tooltip on the outer span isn't cut off. Relies on the
 *  `.cmp-value` / `.cmp-tip` classes from TableTransactionsCompact.css. */
export function ClipValue({ value }: { value?: string }) {
  if (!value) {
    return <span className='cmp-value'>—</span>;
  }
  return (
    <span
      className='tooltip tooltip-top cmp-tip block min-w-0'
      data-tip={value}
    >
      <span className='cmp-value text-start'>{value}</span>
    </span>
  );
}

/** Memo decision as a boolean: true keeps the original QBO memo, false pushes
 *  the client's memo. Shared by the per-row review and the bulk-edit bar. */
export function MemoActionSelect({
  keepOriginal,
  onChange,
  id,
  testId,
  className,
  disabled,
}: {
  keepOriginal: boolean;
  onChange: (keepOriginal: boolean) => void;
  id?: string;
  testId?: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <Select
      size='sm'
      id={id}
      data-testid={testId}
      className={className}
      value={keepOriginal ? 'keep' : 'push'}
      onChange={(v: string) => onChange(v === 'keep')}
      disabled={disabled}
    >
      <Select.Option value='push'>Push client memo</Select.Option>
      <Select.Option value='keep'>Keep QBO memo</Select.Option>
    </Select>
  );
}

export function CategoryPicker({
  transaction,
  value,
  onSelected,
}: {
  transaction: QBODetailedTransaction;
  value?: string;
  onSelected: (category: QBOCategory | undefined) => void;
}) {
  return (
    <TransactionAutoComplete
      key={`rev-cat-${transaction.txn_id}-${
        transaction.qbo_company_id as number
      }`}
      value={value}
      onOptionSelected={onSelected}
      clientId={transaction.client_id as number}
      qboCompanyId={transaction.qbo_company_id as number}
    />
  );
}
